import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';
import AnimatedText3D from './AnimatedText3D';

const ProblemRecognitionSection = () => {
  const problems = [
    {
      id: 1,
      icon: "MessageSquare",
      title: "Te veel tegenstrijdig advies",
      description: "Elke aanbieder vertelt iets anders. Wij geven u onafhankelijk advies op basis van uw situatie.",
      stat: "87%",
      statLabel: "voelt zich overweldigd",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      gradientFrom: "from-orange-500",
      gradientTo: "to-orange-600",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80",
      alt: "Confused person looking at multiple documents with conflicting information about home sustainability"
    },
    {
      id: 2,
      icon: "HelpCircle",
      title: "Onzeker over kosten & subsidies",
      description: "Wat kost het écht? Welke subsidies zijn er? Wij rekenen het voor u uit met actuele cijfers.",
      stat: "€3.500",
      statLabel: "gemiddelde besparing/jaar",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      gradientFrom: "from-blue-500",
      gradientTo: "to-blue-600",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80",
      alt: "Person calculating costs and reviewing financial documents with calculator and papers"
    },
    {
      id: 3,
      icon: "AlertTriangle",
      title: "Bang voor verkeerde keuze",
      description: "Alleen geverifieerde aanbieders met echte reviews. Geen cowboys, wel kwaliteit.",
      stat: "4.7/5",
      statLabel: "gemiddelde klantscore",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      gradientFrom: "from-red-500",
      gradientTo: "to-red-600",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      alt: "Worried homeowner reviewing contractor options and making difficult decision about home renovation"
    },
    {
      id: 4,
      icon: "Clock",
      title: "Geen tijd om alles uit te zoeken",
      description: "Wij doen het voorwerk. U krijgt een helder advies en passende aanbieders in 48 uur.",
      stat: "48u",
      statLabel: "tot persoonlijk advies",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      gradientFrom: "from-purple-500",
      gradientTo: "to-purple-600",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80",
      alt: "Busy professional checking watch while juggling multiple tasks and responsibilities"
    }
  ];

  const benefits = [
    { icon: "Shield", text: "100% onafhankelijk advies" },
    { icon: "CheckCircle", text: "Geverifieerde aanbieders" },
    { icon: "TrendingUp", text: "Actuele subsidie-info" },
    { icon: "Users", text: "780 tevreden klanten" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-b from-muted/30 via-background to-muted/20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.05, 0.03],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 -left-20 w-96 h-96 bg-primary rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.03, 0.06, 0.03],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header with enhanced messaging */}
        <motion.div 
          className="text-center mb-12 sm:mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4"
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(var(--primary-rgb, 59, 130, 246), 0)",
                "0 0 0 8px rgba(var(--primary-rgb, 59, 130, 246), 0.1)",
                "0 0 0 0 rgba(var(--primary-rgb, 59, 130, 246), 0)"
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Icon name="Heart" size={16} />
            <span>Herkent u dit?</span>
          </motion.div>
          <AnimatedText3D />
          <p className="text-lg sm:text-xl font-body text-muted-foreground leading-relaxed">
            Verduurzamen kan complex zijn. Daarom helpen wij u met onafhankelijk advies, 
            betrouwbare aanbieders en alle informatie die u nodig heeft.
          </p>
        </motion.div>

        {/* Problem Cards with images */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {problems?.map((problem) => (
            <motion.div
              key={problem?.id}
              variants={itemVariants}
              whileHover={{ 
                y: -16, 
                scale: 1.03,
                rotateX: 5,
                boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 0 15px rgba(59, 130, 246, 0.08)",
                transition: { duration: 0.3, ease: "easeOut" } 
              }}
              className="bg-card border border-border rounded-2xl overflow-hidden shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.2)] transition-all group transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Image Section with Overlay */}
              <div className="relative h-48 overflow-hidden">
                <motion.img
                  src={problem?.image}
                  alt={problem?.alt}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${problem?.gradientFrom} ${problem?.gradientTo} opacity-60 group-hover:opacity-70 transition-opacity`} />
                
                {/* Icon badge on image */}
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 bg-white/95 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                    <Icon name={problem?.icon} size={24} className={problem?.color} />
                  </div>
                </div>

                {/* Stat badge on image */}
                <div className="absolute bottom-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                    <div className={`text-xl font-bold ${problem?.color} leading-none mb-1`}>
                      {problem?.stat}
                    </div>
                    <div className="text-xs text-muted-foreground leading-none">
                      {problem?.statLabel}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-6">
                {/* Title */}
                <h3 className="text-lg font-heading font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {problem?.title}
                </h3>
                
                {/* Description */}
                <p className="text-sm font-body text-muted-foreground leading-relaxed">
                  {problem?.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Benefits Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-card border border-border rounded-2xl p-6 sm:p-8 mb-12 shadow-elevation-1"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits?.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={benefit?.icon} size={20} className="text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  {benefit?.text}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 sm:p-12 text-center shadow-elevation-2"
        >
          <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Klaar om slim te verduurzamen?
          </h3>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Ontvang binnen 48 uur persoonlijk advies op maat, inclusief kostenoverzicht, 
            subsidies en passende aanbieders voor uw woning.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/quote-request-form'}
              className="bg-white text-primary px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Icon name="Sparkles" size={20} />
              Start gratis advies
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('solutions');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Icon name="Search" size={20} />
              Bekijk oplossingen
            </motion.button>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="Check" size={16} />
              <span>Gratis & vrijblijvend</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={16} />
              <span>Binnen 48 uur antwoord</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <Icon name="Shield" size={16} />
              <span>100% onafhankelijk</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemRecognitionSection;